<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, useTemplateRef } from 'vue'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import UIKit from '@pocketprep/ui-kit'
import brandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import AboutAITutorModal from '@/components/Tutor/AboutAITutorModal.vue'
import TutorSessionRating from '@/components/Tutor/TutorSessionRating.vue'
import { tutorModule } from '@/store/tutor/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { userModule } from '@/store/user/module'
import { quizModule } from '@/store/quiz/module'
import { screenModule } from '@/store/screen/module'
import { analyticsModule } from '@/store/analytics/module'
import type { TQuizMode } from '@/store/quiz/state'
import * as utils from '@/utils'
import { router } from '@/router'
import { useRoute } from 'vue-router'

const {
    Button: PocketButton,
    Icon,
    Textarea: PocketTextarea,
    Tooltip,
} = UIKit

const props = withDefaults(
    defineProps<{
        questionIndex?: number | null
        currentQuestion?: Study.Class.QuestionJSON | null
        currentExam?: Study.Class.ExamMetadataJSON | ClientExam | null
        quizMode?: TQuizMode | null
        explanationPromptToSend?: string | null
        learningTagQuestion?: boolean
        currentQuestionAnswerSeed?: number[] | null
    }>(),
    {
        questionIndex: null,
        currentQuestion: null,
        currentExam: null,
        quizMode: null,
        explanationPromptToSend: null,
        learningTagQuestion: false,
        currentQuestionAnswerSeed: null,
    }
)

const emit = defineEmits<{
    promptMessageSent: []
}>()

const route = useRoute()

const isLoading = ref(true)
const showAITutorModal = ref(false)
const srAnnouncement = ref('')
const showReferences = ref(false)
const requestText = ref('')
const trimmedRequestText = computed(() => requestText.value.trim())
const showMinimizeTooltip = ref(false)
const textareaRef = useTemplateRef('textareaRef')
const tutorChatMessages = useTemplateRef<HTMLElement>('tutor-chat-messages')

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const timeOfDayGreeting = computed(() => {
    const timeOfDay = utils.timeOfDay()
    if (timeOfDay === 'early') {
        return 'morning'
    }
    if (timeOfDay === 'midnight') {
        return 'evening'
    }
    return timeOfDay
})

const activeQuiz = computed(() => {
    return quizModule.getters.getActiveQuiz()
})

const activeQuestionIndex = computed(() => {
    return activeQuiz.value?.currentQuestionIndex
})

const chat = computed(() => {
    return tutorModule.state.chat.length
        ? tutorModule.state.chat.map(c => {
            const ctaAttributes = getCTAAttributes(c.content)

            return {
                ...c,
                ctaAttributes,
            }
        })
        : []
})

const chatCost = computed(() => {
    return tutorModule.state.cost
})

const nudgePrimingMessageType = computed(() => {
    return tutorModule.state.nudgePrimingMessageType
})

const primingMessage = computed(() => {
    if (nudgePrimingMessageType.value === 'eya') {
        return 'Why do you think this is the answer? Explain your thinking before checking.'
    }

    if (nudgePrimingMessageType.value === 'incorrectAnswer') {
        return 'Do you understand why this is the correct answer?'
    }

    if (nudgePrimingMessageType.value === 'stumped') {
        return 'Want some help breaking this question down?'
    }

    if (chat.value.length !== 0 || props.learningTagQuestion) {
        return null
    }

    const greetingQuestionIndex = tutorModule.state.greetingQuestionIndex
    if (
        !tutorModule.state.hasOpenedChatDuringQuiz
        || activeQuestionIndex.value !== greetingQuestionIndex
        || route.name !== 'quiz'
    ) {
        return null
    }

    return `Good ${timeOfDayGreeting.value}! Want to talk through this question?`
})

const isTutorResponding = computed(() => {
    return tutorModule.state.isTutorResponding
})

const isLoadingSessionHistory = computed(() => {
    return tutorModule.state.isLoadingSessionHistory
})

const questionReferences = computed(() => {
    return props.currentQuestion?.references
})

const scannedTextBookReferenceError = computed(() => {
    return tutorModule.state.textbookSearch.error
})

const isLoadingTextBookReferences = computed(() => {
    return tutorModule.state.textbookSearch.isLoading
})

const pageReferences = computed(() => {
    return tutorModule.getters.getPageReferences()
})

const groupedPageReferences = computed(() => {
    const references = pageReferences.value
    if (!references.length) {
        return null
    }

    const parseCitation = (citation: string) => {
        const parts = citation.split(/<\/?i>/i)
        return {
            bookTitle: parts[1]?.trim() ?? '',
            page: parts[2]?.trim() ?? '',
        }
    }

    const parsedRefs = references.map(reference => ({ ...reference, ...parseCitation(reference.citation) }))
    const uniqueBooks = [ ...new Set(parsedRefs.map(r => r.bookTitle)) ]
    const isGrouped = uniqueBooks.length === 1

    const sortByPage = (refs: typeof parsedRefs) => refs.sort((a, b) => {
        const pageA = parseInt(a.page.replace(/\D/g, ''), 10)
        const pageB = parseInt(b.page.replace(/\D/g, ''), 10)
        return pageA - pageB
    })

    if (isGrouped) {
        sortByPage(parsedRefs)
    }

    const groupedBooks = uniqueBooks.map(bookTitle => {
        const bookRefs = sortByPage(parsedRefs.filter(r => r.bookTitle === bookTitle))
        return {
            bookTitle,
            references: bookRefs,
            isSingleRef: bookRefs.length === 1,
        }
    })

    return {
        isGrouped,
        bookCitation: isGrouped ? uniqueBooks[0] : '',
        references: parsedRefs,
        groupedBooks,
    }
})

const showSessionRating = computed(() => {
    const last = chat.value[chat.value.length - 1]
    return !isTutorResponding.value && chat.value.length > 0 && last?.role === 'assistant'
})

const textareaMinHeight = computed(() => {
    return breakpoint.value === 'black-bear' ? '42px' : '38px'
})

const chatUuid = computed(() => {
    return tutorModule.getters.getChatUuid(props.currentQuestion?.serial, props.currentExam?.objectId)
})

const tutorNameTestVariantLabel = computed(() => {
    return tutorModule.state.tutorNameTestVariantLabel || 'Tutor'
})

onMounted(async () => {
    await Promise.all([
        tutorModule.actions.fetchTutorNameTestVariantLabel(),
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
        tutorModule.actions.fetchTutorSession(),
    ])

    if (props.explanationPromptToSend?.trim()) {
        const explanationPromptRequest = props.explanationPromptToSend.trim()
        tutorModule.actions.sendTutorRequest({
            request: explanationPromptRequest,
            primingMessage: primingMessage.value,
            choiceSeed: props.currentQuestionAnswerSeed,
        })
        emit('promptMessageSent')
    }

    scrollTutorChat()
    if (textareaRef.value) {
        const el = textareaRef.value?.$el?.querySelector?.('.uikit-textarea__textarea') as HTMLTextAreaElement | null
        el?.focus()
    }

    if (primingMessage.value) {
        primingMessageShownEvent()
    }

    isLoading.value = false
})

const scrollTutorChat = () => {
    const el = tutorChatMessages.value
    if (el) {
        el.scrollTop = el.scrollHeight
    }
}

const toggleTutorChat = () => {
    tutorModule.actions.toggleTutorChat(props.currentQuestion?.serial, props.currentExam?.objectId)
}

const focusInput = () => {
    nextTick(() => {
        const textareaEl = textareaRef.value?.$el?.querySelector?.(
            '.uikit-textarea__textarea'
        ) as HTMLTextAreaElement | null
        textareaEl?.focus()
    })
}

// Expose focusInput so that the Quiz component can set the focus
// on the Textarea when a user clicks on the Tutor Icon in TutorExplanationPrompts
defineExpose({
    focusInput,
})

const adjustTextarea = () => {
    const el = textareaRef.value?.$el?.querySelector?.('.uikit-textarea__textarea') as HTMLTextAreaElement | null
    if (!el) {
        return
    }
    const computedMinHeight = window.getComputedStyle(el).minHeight
    const minHeight = computedMinHeight && computedMinHeight !== 'auto'
        ? computedMinHeight
        : textareaMinHeight.value
    el.style.height = minHeight
    nextTick(() => {
        if (!el.value?.trim()) {
            el.style.height = minHeight
            return
        }
        el.style.height = `${el.scrollHeight}px`
    })
}

const sendRequest = () => {
    if (!trimmedRequestText.value || trimmedRequestText.value.length > utils.tutorInputMaxLength) {
        return
    }
    const userRequest = trimmedRequestText.value

    requestText.value = ''
    tutorModule.actions.sendTutorRequest({
        request: userRequest,
        primingMessage: primingMessage.value,
        choiceSeed: props.currentQuestionAnswerSeed,
    })
    tutorModule.state.nudgePrimingMessageType = null
    adjustTextarea()
}

const toggleReferences = () => {
    showReferences.value = !showReferences.value

    if (showReferences.value && groupedPageReferences.value?.references) {
        analyticsModule.actions.amplitudeTrack('Tutor References Viewed', {
            chatUuid: chatUuid.value || '',
            questionSerial: props.currentQuestion?.serial,
            examGuid: activeQuiz.value?.examGuid,
            referenceCount: groupedPageReferences.value?.references.length,
        })
    }
}

const primingMessageShownEvent = () => {
    analyticsModule.actions.amplitudeTrack('Tutor Priming Message Received', {
        chatUuid: chatUuid.value || '',
        cost: chatCost.value,
        examMetadataId: props.currentExam?.objectId || undefined,
        examName: props.currentExam?.nativeAppName || '',
        quizMode: props.quizMode ? Number(utils.getNumericQuizMode(props.quizMode)) : 10000,
        serial: props.currentQuestion?.serial,
        primingType: nudgePrimingMessageType.value === 'eya'
            ? 'Explain Your Answer'
            : nudgePrimingMessageType.value === 'incorrectAnswer'
                ? 'Incorrect Answer'
                : nudgePrimingMessageType.value === 'stumped'
                    ? 'Stumped'
                    : 'General',
    })
}

const rateSession = (rating: -1 | 0 | 1) => {
    tutorModule.actions.rateTutorSession(rating)

    analyticsModule.actions.amplitudeTrack(
        'Tutor Chat Rated',
        {
            examMetadataId: props.currentExam?.objectId || '',
            examName: props.currentExam?.nativeAppName || '',
            serial: props.currentQuestion?.serial || '',
            quizMode: props.quizMode ? Number(utils.getNumericQuizMode(props.quizMode)) : 10000,
            chatUuid: chatUuid.value || '',
            chatRating: rating === 1
                ? 'Thumbs Up'
                : rating === -1
                    ? 'Thumbs Down'
                    : 'None',
            cost: tutorModule.state.cost || 0,
        }
    )
}

const getCTAAttributes = (toolContent: string) => {
    if (!/\{\{cta.*\}\}/.test(toolContent)) {
        return
    }

    // pull attributes off of CTA block and convert to object for easy access
    const attrGroups = [ ...toolContent.matchAll(/([a-z0-9]+)="([^"]*)"/ig) ]
    const attributes = attrGroups.reduce<Record<string, string>>((acc, ag) => {
        acc[ag[1]] = ag[2]
        return acc
    }, {})
    const action = attributes['action']

    // if no action is specified, this is an invalid CTA
    if (!action) {
        return
    }

    return attributes
}

const startDraftQuiz = async (draftQuizId: string) => {
    const questions = await quizModule.actions.fetchAnsweredQuestions(draftQuizId)

    if (!questions) {
        throw new Error('Unable to fetch questions for tutor quiz.')
    }

    if (!props.currentExam) {
        throw new Error('No current exam found.')
    }

    const answerSeeds = utils.generateAnswerSeeds(questions)
    quizModule.actions.updateActiveQuiz({
        questions,
        examGuid: props.currentExam.examGuid,
        answers: [],
        mode: 'custom',
        startedAt: utils.localISODateString(),
        currentQuestionIndex: 0,
        durationSeconds: 0,
        breakDurationSeconds: 0,
        timerType: 'exam',
        answerSeeds,
    })
    router.push({ name: 'quiz' })
}

const startNewSession = async () => {
    isLoading.value = true
    await tutorModule.actions.startNewSession()
    isLoading.value = false
}

// Send a tutor request when a user selects an explanation prompt
// in the TutorExplanationPrompts component
watch(
    () => props.explanationPromptToSend,
    (message) => {
        if (!message?.trim() || isLoading.value) {
            return
        }
        const explanationPromptRequest = message.trim()
        tutorModule.actions.sendTutorRequest({
            request: explanationPromptRequest,
            primingMessage: primingMessage.value,
            choiceSeed: props.currentQuestionAnswerSeed,
        })
        emit('promptMessageSent')
    }
)

watch(chat, () => {
    nextTick(() => setTimeout(() => scrollTutorChat(), 1))
    tutorModule.state.nudgePrimingMessageType = null
}, { deep: true })

watch(activeQuestionIndex, () => {
    requestText.value = ''
    showReferences.value = false
})

watch(breakpoint, () => {
    if (requestText.value) {
        nextTick(() => {
            adjustTextarea()
        })
    } else {
        adjustTextarea()
    }
})

watch(isTutorResponding, (val: boolean, oldVal: boolean) => {
    if (val === true) {
        srAnnouncement.value = 'Tutor is generating a response'
    } else if (oldVal === true && val === false) {
        const lastMessage = chat.value[chat.value.length - 1]
        if (lastMessage?.role === 'assistant') {
            srAnnouncement.value = ''
            nextTick(() => {
                srAnnouncement.value = 'Tutor responded with: ' + lastMessage.content
                    .replace(/\*\*(.+?)\*\*/g, '$1')
                    .replace(/\*(.+?)\*/g, '$1')
                focusInput()
            })
        } else {
            nextTick(() => focusInput())
        }
    }
})

watch(primingMessage, (val: string | null, oldVal: string | null) => {
    if (val && val !== oldVal) {
        // fire amplitude event
        primingMessageShownEvent()
    }
})

// Escape HTML to prevent XSS
// then convert **words** to bold and *words* to italic
// and add paragraph breaks
const formatTutorMessageContent = (raw: string): string => {
    if (!raw) {
        return ''
    }
    const escaped = raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
}
</script>

<template>
    <AboutAITutorModal v-if="showAITutorModal" @close="showAITutorModal = false" />
    <div
        ref="tutorChatRef"
        class="tutor-chat"
        role="region"
        :aria-label="`Tutor chat for question ${ questionIndex }`"
    >
        <header class="tutor-chat__header" v-dark>
            <div
                class="tutor-chat__header-left"
            >
                <Icon
                    type="tutorChat"
                    class="tutor-chat__header-icon"
                />
                <div class="tutor-chat__header-title">Tutor</div>
                <div class="tutor-chat__header-question" v-if="questionIndex !== null">
                    Question {{ questionIndex }}
                </div>
            </div>
            <div class="tutor-chat__header-right">
                <PocketButton class="tutor-chat__new" @click="startNewSession">
                    New
                </PocketButton>
                <PocketButton
                    class="tutor-chat__minimize"
                    aria-label="Close tutor chat"
                    type="icon"
                    icon="minimize"
                    @focus="showMinimizeTooltip = true"
                    @blur="showMinimizeTooltip = false"
                    @mouseenter="showMinimizeTooltip = true"
                    @mouseleave="showMinimizeTooltip = false"
                    @click="toggleTutorChat"
                    @mousedown.prevent
                >
                    <Tooltip
                        v-if="showMinimizeTooltip && breakpoint !== 'black-bear'"
                        class="tutor-chat__minimize-tooltip"
                        :key="'minimize'"
                        :is-dark-mode="isDarkMode"
                        :theme="'leftalign'"
                        :styles="{
                            'backgroundColor': brandColors.fog,
                            'color': brandColors.brandBlack,
                            'white-space': 'nowrap',
                        }"
                    >
                        Minimize Chat
                    </Tooltip>
                </PocketButton>
            </div>
        </header>
        <div
            ref="tutor-chat-messages"
            class="tutor-chat__messages"
            aria-describedby="tutor-chat-intro"
        >
            <div
                v-if="learningTagQuestion && !chat.length"
                id="tutor-chat-intro"
                class="tutor-chat__intro"

            >
                <div class="tutor-chat__intro-seen-before">
                    You’ve seen this question before so you might remember what the correct answer is.
                    Let’s go deeper to make sure you understand why.
                </div>
                <div class="tutor-chat__intro-why-correct">
                    After you select your answer, tell me why you think it is correct.
                </div>
            </div>
            <div
                v-else-if="!chat.length"
                id="tutor-chat-intro"
                class="tutor-chat__intro"
            >
                <template v-if="questionIndex !== null">
                    Each question starts a new chat. You can go back to any question to pick back up or review the chat.
                </template>
                <template v-else>
                    You can ask me about anything related to the exam! Here are tools I have access to:<br>
                    - Reviewing and talking about your study progress<br>
                    - Search the question bank and quiz you on questions I think might be helpful for you<br>
                    - Searching exam textbooks and providing citations<br>
                    - Discussing past chats we've had
                </template>
            </div>
            <div
                v-for="(message, index) in chat"
                :key="message.content"
                class="tutor-chat__message"
                :class="[`tutor-chat__message--${message.role}`]"
            >
                <div
                    class="tutor-chat__message-inner"
                    :class="[`tutor-chat__message-inner--${message.role}`]"
                    v-dark
                >
                    <div
                        v-if="message.content === 'Thinking...' && message.role === 'system'"
                        class="tutor-chat__system-loading-message-inner-text"
                        aria-hidden="true"
                    >
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </div>
                    <div
                        v-else-if="message.role === 'tool_use'"
                        class="tutor-chat__message-tool-use"
                    >
                        <template v-if="message.ctaAttributes">
                            <template v-if="
                                message.ctaAttributes['action'] === 'draftQuiz'
                                    && message.ctaAttributes['draftQuizId']
                            ">
                                <PocketButton
                                    @click="startDraftQuiz(message.ctaAttributes['draftQuizId'])"
                                    :is-dark-mode="isDarkMode"
                                    class="tutor-chat__message-tool-draftQuiz"
                                >
                                    {{ message.ctaAttributes['label'] || 'Start Quiz' }}
                                </PocketButton>
                            </template>
                        </template>
                        <template v-else>
                            {{ message.content }}
                        </template>
                    </div>
                    <div
                        v-else-if="message.role === 'assistant' || message.role === 'user'"
                        class="tutor-chat__message-inner-text"
                        :class="{ 'tutor-chat__message-inner-text--streaming': message.isStreaming }"
                        :aria-live="message.isStreaming ? 'off' : undefined"
                        v-dark
                        v-html="formatTutorMessageContent(message.content)"
                    />
                    <div
                        v-if="
                            !message.isStreaming
                                && ((index === 1 && message.role === 'assistant')
                                    ||(index === 2 && message.role === 'assistant'))
                                && (pageReferences.length || questionReferences?.length)"
                        class="tutor-chat__message-references-wrap"
                        v-dark
                    >
                        <div
                            class="tutor-chat__message-references"
                            role="button"
                            tabindex="0"
                            :aria-expanded="showReferences"
                            @click="toggleReferences"
                            @keydown.enter.prevent="toggleReferences"
                            @keydown.space.prevent="toggleReferences"
                        >
                            {{ showReferences ? 'Hide' : 'Show' }} References
                            <Icon
                                class="tutor-chat__message-references-icon"
                                :class="{ 'tutor-chat__message-references-icon--showing-refs': showReferences }"
                                type="accordionArrow"
                                aria-hidden="true"
                            />
                        </div>
                        <div
                            v-if="showReferences"
                            class="tutor-chat__message-references-content"
                            v-dark
                        >
                            <template v-if="isLoadingTextBookReferences">
                                <div class="tutor-chat__loading-scanned-textbook-references">
                                    Loading textbook references... This can take up to 5-10 seconds.
                                </div>
                            </template>
                            <template
                                v-else-if="pageReferences.length
                                    && groupedPageReferences
                                    && !scannedTextBookReferenceError"
                            >
                                <template v-if="groupedPageReferences.isGrouped">
                                    <div
                                        class="tutor-chat__references-book-title"
                                        v-html="groupedPageReferences.bookCitation"
                                    />
                                    <ul class="tutor-chat__references-book-list">
                                        <li
                                            v-for="reference in groupedPageReferences.references"
                                            :key="reference.citation"
                                            class="tutor-chat__page-reference-item"
                                        >
                                            <div
                                                class="tutor-chat__page-reference-page-number"
                                                v-html="reference.page"
                                            />
                                            <div
                                                class="tutor-chat__page-reference-explanation"
                                            >
                                                {{ reference.explanation }}
                                            </div>
                                        </li>
                                    </ul>
                                </template>
                                <template v-else>
                                    <ul class="tutor-chat__references-book-list-multiple-books">
                                        <li
                                            v-for="book in groupedPageReferences.groupedBooks"
                                            :key="book.bookTitle"
                                            class="tutor-chat__page-reference-item"
                                        >
                                            <template v-if="book.isSingleRef">
                                                <div
                                                    class="tutor-chat__page-reference-citation"
                                                    v-html="book.references[0].citation"
                                                />
                                                <div class="tutor-chat__page-reference-explanation">
                                                    {{ book.references[0].explanation }}
                                                </div>
                                            </template>
                                            <template v-else>
                                                <div
                                                    class="tutor-chat__page-reference-book-title"
                                                    v-html="book.bookTitle"
                                                />
                                                <ul class="tutor-chat__references-sub-list">
                                                    <li
                                                        v-for="reference in book.references"
                                                        :key="reference.citation"
                                                        class="tutor-chat__page-reference-sub-item"
                                                    >
                                                        <div
                                                            class="tutor-chat__page-reference-page-number"
                                                            v-html="reference.page"
                                                        />
                                                        <div class="tutor-chat__page-reference-explanation">
                                                            {{ reference.explanation }}
                                                        </div>
                                                    </li>
                                                </ul>
                                            </template>
                                        </li>
                                    </ul>
                                </template>
                            </template>
                            <template v-else>
                                <div
                                    v-for="reference in questionReferences"
                                    :key="reference"
                                    class="tutor-chat__question-references"
                                    v-html="reference"
                                />
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            <TutorSessionRating
                v-if="showSessionRating"
                class="tutor-chat__rating"
                @ratingSelected="rateSession"
            />
            <div
                v-if="primingMessage"
                class="tutor-chat__primer-message"
                aria-live="polite"
            >
                {{ primingMessage }}
            </div>
        </div>
        <div
            class="tutor-chat__sr-live-region"
            aria-live="polite"
            aria-atomic="true"
        >{{ srAnnouncement }}</div>
        <div class="tutor-chat__footer">
            <div class="tutor-chat__input-row" v-dark>
                <div
                    class="tutor-chat__input-wrap"
                    @keydown.stop
                    @keyup.stop
                >
                    <PocketTextarea
                        ref="textareaRef"
                        v-model="requestText"
                        class="tutor-chat__input"
                        :placeholder="chat.length || nudgePrimingMessageType ? 'Reply...' : 'Ask something...'"
                        aria-label="Message Tutor"
                        :auto-focus="true"
                        :is-dark-mode="true"
                        :height="textareaMinHeight"
                        width="100%"
                        :disabled="isTutorResponding || isLoadingSessionHistory"
                        @keydown.enter.exact.prevent="sendRequest"
                        @input="adjustTextarea"
                    />
                    <PocketButton
                        class="tutor-chat__send"
                        aria-label="Send message"
                        :disabled="!trimmedRequestText || trimmedRequestText.length > utils.tutorInputMaxLength
                            || isTutorResponding || isLoadingSessionHistory"
                        @click="sendRequest"
                    >
                        <Icon type="arrow" class="tutor-chat__send-icon" />
                    </PocketButton>
                </div>
            </div>
            <div
                class="tutor-chat__input-length-warning"
                :class="{
                    'tutor-chat__input-length-warning--visible':
                        trimmedRequestText.length > utils.tutorInputMaxLength,
                }"
                role="status"
                aria-live="polite"
            >
                <template v-if="trimmedRequestText.length > utils.tutorInputMaxLength">
                    Message too long: {{ trimmedRequestText.length }} / {{ utils.tutorInputMaxLength }} characters
                </template>
            </div>
            <div class="tutor-chat__tutor-ai-disclaimer">
                <PocketButton
                    class="tutor-chat__tutor-ai-disclaimer-btn"
                    aria-label="Open AI disclaimer"
                    type="tertiary"
                    :is-dark-mode="true"
                    @click="showAITutorModal = true"
                    @mousedown.prevent
                >
                    {{ tutorNameTestVariantLabel }}
                </PocketButton> is powered by AI.
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.tutor-chat {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    container-type: size;

    @keyframes ai-feedback-loading {
        0% {
            opacity: 1;
        }
        50% {
            opacity: 0.2;
        }
        100% {
            opacity: 0.2;
        }
    }

    @keyframes blink-cursor {
        50% {
            opacity: 0;
        }
    }

    &__header {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 12px;
        height: 44px;
        color: #fff;
    }

    &__header-left,
    &__header-right {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__header-icon {
        flex-shrink: 0;
        width: 27px;
    }

    &__header-title,
    &__header-question {
        color: $white;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
    }

    &__header-question {
        color: rgba($white, 0.70);
        margin-top: 1px;
        margin-left: -2px;
    }

    &__header-new {
        float: right;
    }

    &__new {
        padding: 6px 10px;
    }

    &__minimize {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        padding: 0;

        :deep(svg) {
            color: $banana-bread
        }

        &:hover {
            background: $brand-black;
        }
    }

    &__minimize-tooltip {
        position: absolute;
        top: -34px;
        left: 12px;
    }

    &__messages {
        flex: 1;
        overflow: auto;
        padding: 15px 9px 30px;
        border-top: 1px solid rgba($pewter, 0.30);
        border-bottom: 1px solid rgba($pewter, 0.30);
    }

    &__intro {
        margin: 0 0 12px;
        padding: 0 6px;
        color: $cloudy;
        font-size: 13px;
        font-weight: 400;
        line-height: 18px;
    }

    &__intro-why-correct {
        margin: 8px 0 12px;
    }

    &__primer-message {
        padding: 0 6px;
        margin-bottom: 12px;
        color: $white;
        font-size: 15px;
        font-weight: 500;
        line-height: 18px;
    }

    &__message {
        margin-bottom: 12px;
        padding: 6px 16px 6px 6px;

        &--user {
            text-align: right;
            padding: 0;
        }

        &--assistant {
            text-align: left;
        }

        &--tool_use {
            font-size: 13px;
            font-style: italic;
            margin-bottom: 12px;
            padding: 2px 16px 2px 6px;
            color: $cloudy;
        }
    }

    &__message-inner {
        border-radius: 10px;
        display: inline-block;
        max-width: 80%;
        text-align: left;

        &--user {
            background-color: rgba($buttermilk, 0.20);
            padding: 8px 12px;
        }

        &--assistant {
            max-width: 342px;
        }
    }

    &__system-loading-message-inner-text {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        max-width: none;
        font-weight: 500;
        letter-spacing: -0.1px;
        line-height: 0;

        span {
            position: relative;
            display: inline-block;
            width: 6px;
            height: 6px;
            overflow: visible;
        }

        span::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: rgba($pewter, 0.85);
            transform: scale(1);
            transform-origin: center;
        }

        span:first-child {
            animation-name: ai-feedback-loading;
            animation-duration: 1.2s;
            animation-iteration-count: infinite;
            animation-delay: 0s;
            animation-timing-function: linear;
        }

        span:nth-child(2) {
            animation-name: ai-feedback-loading;
            animation-duration: 1.2s;
            animation-iteration-count: infinite;
            animation-delay: 0.3s;
            animation-timing-function: linear;
        }

        span:nth-child(3) {
            animation-name: ai-feedback-loading;
            animation-duration: 1.2s;
            animation-iteration-count: infinite;
            animation-delay: 0.6s;
            animation-timing-function: linear;
        }

        :deep(p) {
            margin: 0 0 10px;
        }
    }

    &__message-inner-text {
        color: $white;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;

        &--streaming::after {
            content: '|';
            display: inline-block;
            margin-left: 2px;
            color: $brand-blue;
            font-weight: 600;
            animation: blink-cursor 1s linear infinite;
        }

        :deep(reference) {
            margin-bottom: 6px;
            display: list-item;
            box-sizing: border-box;
            font-size: 13px;
            line-height: 14px;
            margin-top: 10px;
            font-style: italic;
            margin-left: 15px;
        }
    }

    &__message-references {
        margin: 12px 0 8px 0;
        color: $banana-bread;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        cursor: pointer;

        &:hover {
            color: $butterscotch;
        }
    }

    &__message-references-icon {
        margin-left: 5px;

        &--showing-refs {
            transform: rotate(180deg);
        }
    }

    &__message-references-wrap {
        margin-top: 18px;
    }

    &__message-references-content {
        width: 100%;
        padding-right: 16px;
        color: $pearl;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
    }

    &__question-references {
        :deep(p) {
            font-style: italic;
            margin-top: 6px;
            margin-bottom: 10px;
        }
    }

    &__references-book-title {
        margin-bottom: 6px;
        font-style: italic;
    }

    &__references-book-list,
    &__references-book-list-multiple-books {
        margin: 0;
        padding-left: 20px;
        list-style: disc;
    }

    &__references-sub-list {
        margin: 4px 0 0;
        padding-left: 20px;
        list-style: disc;
    }

    &__page-reference-item {
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__page-reference-sub-item {
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__page-reference-book-title {
        font-style: italic;
    }

    &__page-reference-explanation {
        margin: 6px 0 0;

    }

    &__rating {
        margin-bottom: 16px;
    }

    &__sr-live-region {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        white-space: nowrap;
        border: 0;
    }

    &__footer {
        flex-shrink: 0;
        padding: 15px 15px 12px;
    }

    &__input-row {
        display: flex;
        margin-bottom: 8px;
    }

    &__input-length-warning {
        &--visible {
            color: $banana-bread;
            font-size: 12px;
            font-weight: 500;
            margin: -4px 4px 8px;
        }
    }

    &__input-wrap {
        position: relative;
        flex: 1;
    }

    &__input {
        :deep(.uikit-textarea__textarea) {
            min-height: 38px;
            max-height: 50cqh;
            resize: none;
            overflow-y: auto;
            background-color: $moonlit-ocean;
            color: $white;
            line-height: 1.25;
            padding: 8px 42px 8px 12px;
            font-size: 15px;
            border-radius: 6px;

            &::placeholder {
                color: rgba($white, 0.70);
            }

            @include breakpoint(black-bear) {
                height: 42px;
                min-height: 42px;
                padding: 11px 42px 11px 12px;
                border-radius: 5px;
            }

            &::-webkit-scrollbar {
                width: 0;
                height: 0;
            }
        }
    }

    &__send {
        position: absolute;
        right: 4px;
        bottom: 9px;
        z-index: 2;
        flex-shrink: 0;
        width: 30px;
        height: 29px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $banana-bread;
        border: none;
        border-radius: 4px;
        color: #fff;
        cursor: pointer;

        &:hover:not(:disabled) {
            background-color: $butterscotch;
        }

        &:disabled {
            background-color: rgba($banana-bread, 0.40);
        }

        :deep(svg) {
            margin-top: 4px;
            width: 16px;
            height: 13px;
        }

        @include breakpoint(black-bear) {
            width: 38px;
            height: 35px;
            bottom: 8px;
            border-radius: 3px;

            :deep(svg) {
                margin-top: 3px;
                width: 19px;
                height: 19px;
            }
        }
    }

    &__send-icon {
        transform: rotate(-90deg);
        color: $brand-black;
    }

    &__tutor-ai-disclaimer {
        text-align: center;
        color: $cloudy;
        font-size: 12px;
        font-weight: 400;
    }

    &__tutor-ai-disclaimer-btn {
        color: $banana-bread;
        font-size: 12px;
        font-weight: 500;

        &:hover {
            color: $butterscotch;
        }
    }

    :deep(.uikit-btn--icon) {
        display: flex;
        align-items: flex-start;
        padding: 0 4px;
    }
}
</style>
